import type { ListingHealth } from '@ralph/contracts'
import clsx from 'clsx'
import { CircleCheck, TriangleAlert } from 'lucide-react'
import { describeIssue, useI18n } from '@/i18n/index.ts'

/**
 * A row's identity, now that its sentence is no longer part of it.
 *
 * It used to be `code:message` — the English prose, which is the one thing this panel has
 * stopped rendering, and which would have changed identity under a reader who switched
 * language if it ever became the translated sentence. Code, key and the values the sentence
 * interpolates say the same thing without the words: two `capability_disabled` findings about
 * two different capabilities stay two rows.
 */
const rowKey = (issue: ListingHealth['issues'][number]): string =>
  [issue.code, issue.messageKey ?? '', ...Object.values(issue.params ?? {})].join(':')

/**
 * What is wrong with this agent, and what the owner can do about it.
 *
 * Health is computed server-side and shown only to the owner and administrators — consumers
 * get graceful degradation, never a defect report (AGM-048). Each issue pairs an icon with
 * a heading and a remedy in the maker's own vocabulary, and between them the finding itself,
 * which is the only one of the three that can name the actual base or capability that broke
 * rather than the class of thing.
 *
 * That middle line used to be the server's English, rendered verbatim on every screen in the
 * product: eleven sentences, computed in `domain/marketplace/health.ts`, shown to every agent
 * owner, and invisible to the ratchet that guards this rule because they arrived on a field
 * called `message` of a record called `issue` rather than on an error (PRD-27 LOC-033). The
 * finding now carries a key and is rendered from the catalogue, falling back to the stored
 * prose for a snapshot written before the key existed — which is up to five minutes of them
 * at any moment, since health is recomputed lazily.
 */
export const HealthPanel = ({ health }: { health: ListingHealth }) => {
  // Bound inside the component, never at module scope: a captured formatter — or a captured
  // catalogue branch — keeps the locale that was active when the module first evaluated and
  // stops following the switch (PRD-27 F-3).
  const { formatAgo, t } = useI18n()
  const strings = t.marketplace.maker.health
  const ok = health.status === 'ok'

  return (
    <div className="card rounded-panel px-3.5 py-3">
      <header className="flex items-center gap-2.5">
        {/* The tile takes the colour so the heading beside it does not have to: a coloured
            heading reads as an error message even when the news is good. */}
        <span
          aria-hidden
          className={clsx(
            'flex h-[26px] w-[26px] flex-none items-center justify-center rounded-tile border border-rule',
            ok ? 'bg-grounded-soft text-grounded' : 'bg-alert-soft text-alert'
          )}
        >
          {ok ? <CircleCheck size={13} strokeWidth={2.2} /> : <TriangleAlert size={13} strokeWidth={2.2} />}
        </span>
        <h2 className="min-w-0 flex-1 font-sans text-[14px] font-semibold tracking-[-0.018em]">
          {ok ? strings.ok : strings.title}
        </h2>
        <span className="readout shrink-0 text-fg-subtle">{strings.checkedAt(formatAgo(health.computedAt))}</span>
      </header>

      {health.issues.length === 0 ? (
        <p className="mt-2.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.okBody}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {health.issues.map((issue) => (
            <li key={rowKey(issue)} className="rounded-tile border border-rule bg-card-solid px-3 py-2.5">
              <p className="flex items-start gap-2 text-[13px] font-semibold tracking-[-0.014em]">
                <span className="mt-[3px] shrink-0 text-alert" aria-hidden>
                  <TriangleAlert size={12} strokeWidth={2.2} />
                </span>
                {strings.heading[issue.code]}
              </p>
              {/* The stored English is the fallback and never the first choice. It is not a
                  defect that it is here: it is the deploy-order guarantee, the same one
                  `describeApiError` makes, and the reason the server keeps writing prose
                  beside the key at all. */}
              <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                {describeIssue(t, issue) ?? issue.message}
              </p>
              {/* No `?? issue.code` and no `?? ''` any more: both maps are keyed by the server's
                  own enum, so an unmapped code is a compile error here rather than the raw token
                  `owner_deactivated` in a heading and a silently blank remedy under it (D-9). */}
              <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5]">{strings.remedy[issue.code]}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
